import { Button, Grid } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { i18nToast } from '../../helpers/i18nToast'
import toastError from '../../errors/toastError'
import useSettings from '../../hooks/useSettings'
import { InputFieldText } from '../InputFieldText'
import InputMessage from '../InputMessage'
import { InputSwitch } from '../InputSwitch'
import { i18n } from '../../translate/i18n.js'
import { useStyles } from './styles.js'

const DEFAULT_SYSTEM_OUT_OF_HOURS_MESSAGE =
  '*Mensagem Automática:*\n\nEstamos fora do horário de expediente'
const DEFAULT_SYSTEM_ASK_CONTACT_APELIDO_MESSAGE =
  '*Mensagem Automática:*\n\nPara continuar o atendimento, informe com quem estamos falando.'
const DEFAULT_SYSTEM_ASK_CONTACT_NAME_MESSAGE =
  '*Mensagem Automática:*\n\nNão conseguimos identificar seu nome corretamente. Por favor, responda com seu nome para continuarmos o atendimento.'
const DEFAULT_SYSTEM_OPEN_TICKET_TIMEOUT_WARNING_MESSAGE =
  '⚠️ Em 1 minuto este atendimento será finalizado por falta de interação. Envie uma mensagem para continuar o atendimento.'

const isEnabledValue = value => {
  if (value === undefined || value === null) return false

  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0' && normalized !== 'disabled'
}

const getSettingValue = (settings, key, fallback) => {
  const setting = settings.find(s => s.key === key)
  const value = setting?.value

  if (typeof value === 'string' && value.trim() !== '') {
    return value
  }

  return fallback
}

export default function SystemMessages({ settings = [] }) {
  const classes = useStyles()
  const { update } = useSettings()

  const [systemOutOfHoursMessage, setSystemOutOfHoursMessage] = useState(
    DEFAULT_SYSTEM_OUT_OF_HOURS_MESSAGE
  )
  const [ticketMessageSwitch, setTicketMessageSwitch] = useState('disabled')
  const [ticketAcceptedMessage, setTicketAcceptedMessage] = useState('')
  const [transferMessageSwitch, setTransferMessageSwitch] = useState('disabled')
  const [transferMessage, setTransferMessage] = useState('')
  const [systemAskContactApelidoMessage, setSystemAskContactApelidoMessage] =
    useState(DEFAULT_SYSTEM_ASK_CONTACT_APELIDO_MESSAGE)
  const [systemAskContactNameMessage, setSystemAskContactNameMessage] =
    useState(DEFAULT_SYSTEM_ASK_CONTACT_NAME_MESSAGE)
  const [
    systemOpenTicketTimeoutWarningMessage,
    setSystemOpenTicketTimeoutWarningMessage
  ] = useState(DEFAULT_SYSTEM_OPEN_TICKET_TIMEOUT_WARNING_MESSAGE)

  useEffect(() => {
    if (!Array.isArray(settings) || settings.length === 0) {
      return
    }

    setSystemOutOfHoursMessage(
      getSettingValue(
        settings,
        'systemOutOfHoursMessage',
        DEFAULT_SYSTEM_OUT_OF_HOURS_MESSAGE
      )
    )
    setTicketAcceptedMessage(getSettingValue(settings, 'ticketAcceptedMessage', ''))
    setTransferMessage(getSettingValue(settings, 'transferMessage', ''))
    setTicketMessageSwitch(
      isEnabledValue(getSettingValue(settings, 'ticketAcceptedMessage', ''))
        ? 'enabled'
        : 'disabled'
    )
    setTransferMessageSwitch(
      isEnabledValue(getSettingValue(settings, 'transferMessage', ''))
        ? 'enabled'
        : 'disabled'
    )
    setSystemAskContactApelidoMessage(
      getSettingValue(
        settings,
        'systemAskContactApelidoMessage',
        DEFAULT_SYSTEM_ASK_CONTACT_APELIDO_MESSAGE
      )
    )
    setSystemAskContactNameMessage(
      getSettingValue(
        settings,
        'systemAskContactNameMessage',
        DEFAULT_SYSTEM_ASK_CONTACT_NAME_MESSAGE
      )
    )
    setSystemOpenTicketTimeoutWarningMessage(
      getSettingValue(
        settings,
        'systemOpenTicketTimeoutWarningMessage',
        DEFAULT_SYSTEM_OPEN_TICKET_TIMEOUT_WARNING_MESSAGE
      )
    )
  }, [settings])

  const handleSave = async () => {
    try {
      await Promise.all([
        update({
          key: 'ticketAcceptedMessage',
          value: ticketAcceptedMessage
        }),
        update({
          key: 'transferMessage',
          value: transferMessage
        }),
        update({
          key: 'systemOutOfHoursMessage',
          value: systemOutOfHoursMessage
        }),
        update({
          key: 'systemAskContactApelidoMessage',
          value: systemAskContactApelidoMessage
        }),
        update({
          key: 'systemAskContactNameMessage',
          value: systemAskContactNameMessage
        }),
        update({
          key: 'systemOpenTicketTimeoutWarningMessage',
          value: systemOpenTicketTimeoutWarningMessage
        })
      ])

      i18nToast.success('settings.success')
    } catch (err) {
      toastError(err)
    }
  }

  const handleTicketMessageSwitch = async value => {
    setTicketMessageSwitch(value)

    if (value === 'disabled') {
      setTicketAcceptedMessage('')
      await update({
        key: 'ticketAcceptedMessage',
        value: ''
      })
    }
  }

  const handleTransferMessageSwitch = async value => {
    setTransferMessageSwitch(value)

    if (value === 'disabled') {
      setTransferMessage('')
      await update({
        key: 'transferMessage',
        value: ''
      })
    }
  }

  return (
    <Grid container>
      <Grid item xs={12} className={classes.boxGridService}>
        <h3 className={classes.groupTitle}>
          {i18n.t('settings.SystemMessages.title')}
        </h3>
        <p className={classes.helpText}>
          {i18n.t('settings.systemMessages.description')}
        </p>

        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.boxContentInputMessage}>
            <Grid xs={12} md={6} item className={classes.boxContentGroup}>
              <InputSwitch
                label={'Mensagem de Saudação'}
                name="ticketMessageSwitch"
                id="ticketMessageSwitch"
                size="small"
                checked={ticketMessageSwitch === 'enabled'}
                onChange={e => {
                  handleTicketMessageSwitch(
                    e.target.checked ? 'enabled' : 'disabled'
                  )
                }}
              />
              {ticketMessageSwitch === 'enabled' && (
                <>
                  <InputMessage
                    label={
                      'Mensagem de saudação padrão para departamentos sem mensagem definida'
                    }
                    value={ticketAcceptedMessage}
                    onChange={e => setTicketAcceptedMessage(e.target.value)}
                    onBlur={() => {
                      update({
                        key: 'ticketAcceptedMessage',
                        value: ticketAcceptedMessage
                      })
                    }}
                    placeholder={
                      'Olá! Bem-vindo ao [Nome da Empresa]. Como posso ajudar você hoje?'
                    }
                    isLarge
                  />
                  <span className={classes.description}>
                    Caso o departamento já possua mensagem de saudação
                    configurada, ela terá prioridade e esta será ignorada.
                  </span>
                </>
              )}
            </Grid>
            <Grid xs={12} md={6} item className={classes.boxContentGroup}>
              <InputSwitch
                label={'Mensagem de Transferência'}
                name="transferMessageSwitch"
                id="transferMessageSwitch"
                size="small"
                checked={transferMessageSwitch === 'enabled'}
                onChange={e => {
                  handleTransferMessageSwitch(
                    e.target.checked ? 'enabled' : 'disabled'
                  )
                }}
              />
              {transferMessageSwitch === 'enabled' && (
                <>
                  <InputMessage
                    label={
                      'Mensagem de transferência padrão para departamentos sem mensagem definida'
                    }
                    value={transferMessage}
                    onChange={e => {
                      setTransferMessage(e.target.value)
                    }}
                    onBlur={() => {
                      update({
                        key: 'transferMessage',
                        value: transferMessage
                      })
                    }}
                    placeholder={
                      'Aguarde um momento que seu atendimento foi transferido.'
                    }
                    isLarge
                  />
                  <span className={classes.description}>
                    Caso o departamento já possua mensagem de transferência
                    configurada, ela terá prioridade e esta será ignorada.
                  </span>
                </>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              name="systemOutOfHoursMessage"
              label={i18n.t('settings.systemMessages.outOfHoursMessage')}
              value={systemOutOfHoursMessage}
              onChange={e => setSystemOutOfHoursMessage(e.target.value)}
              multiline
              rows={3}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              name="systemAskContactApelidoMessage"
              label={i18n.t('settings.systemMessages.askContactApelidoMessage')}
              value={systemAskContactApelidoMessage}
              onChange={e => setSystemAskContactApelidoMessage(e.target.value)}
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              name="systemAskContactNameMessage"
              label={i18n.t('settings.systemMessages.askContactNameMessage')}
              value={systemAskContactNameMessage}
              onChange={e => setSystemAskContactNameMessage(e.target.value)}
              multiline
              rows={4}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              name="systemOpenTicketTimeoutWarningMessage"
              label={i18n.t('settings.systemMessages.openTicketTimeoutWarningMessage')}
              value={systemOpenTicketTimeoutWarningMessage}
              onChange={e =>
                setSystemOpenTicketTimeoutWarningMessage(e.target.value)
              }
              multiline
              rows={3}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              {i18n.t('settings.systemMessages.buttons.save')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
